import { ExpressionSpecification, StyleSpecification } from 'maplibre-gl';
import { MapLanguageMode } from '../../../common/enums';
import { MapSourceInfo, MapSourceUrlReplacementInfo } from '../../../types/other';

const NAME_LAYER_MARKER = 'name:latin';

export function buildNameExpression(mode: MapLanguageMode, locale?: string): ExpressionSpecification {
    const englishFallback: ExpressionSpecification = [
        'coalesce',
        ['get', 'name:en'],
        ['get', 'name_en'],
        ['get', 'name:latin'],
        ['get', 'name']
    ];

    switch (mode) {
        case MapLanguageMode.device: {
            const lang = locale ?? 'en';
            return ['coalesce', ['get', `name:${lang}`], ['get', 'name:en'], ['get', 'name:latin'], ['get', 'name']];
        }
        case MapLanguageMode.localLatin:
            return ['coalesce', ['get', 'name:latin'], ['get', 'name:en'], ['get', 'name']];
        case MapLanguageMode.localNative:
            return ['coalesce', ['get', 'name'], ['get', 'name:latin'], ['get', 'name:en']];
        case MapLanguageMode.bilingual:
            return [
                'case',
                ['all', ['has', 'name:nonlatin'], ['!=', ['get', 'name:nonlatin'], '']],
                ['concat', ['coalesce', ['get', 'name:en'], ['get', 'name:latin'], ['get', 'name']], '\n', ['get', 'name:nonlatin']],
                englishFallback
            ];
        case MapLanguageMode.english:
        default:
            return englishFallback;
    }
}

export function applyLanguageToStyle(style: StyleSpecification, mode: MapLanguageMode, locale?: string): string[] {
    const expression = buildNameExpression(mode, locale);
    const patchedIds: string[] = [];

    for (const layer of style.layers) {
        if (!('layout' in layer) || !layer.layout) {
            continue;
        }
        const layout = layer.layout as Record<string, unknown>;
        const tf = layout['text-field'];
        if (typeof tf === 'string' && tf.includes(NAME_LAYER_MARKER)) {
            layout['text-field'] = expression;
            patchedIds.push(layer.id);
        }
    }

    return patchedIds;
}

function patchUrlWithToken(url: string, host: string, urlReplacement: string, token: string): string | null {
    if (!url || !urlReplacement) {
        return null;
    }
    const parts = url.split('/');

    // 2 protocol + 1 path (at least) + 3 in file template
    if (parts.length < 6) {
        return null;
    }

    // If url doesn't contain token, return original url
    if (parts[parts.length - 4] !== token) {
        return url;
    }

    parts[parts.length - 4] = urlReplacement;
    parts[2] = host;
    return parts.join('/');
}

function updateSourceUrl(style: StyleSpecification, sourceId: string, host: string, info: MapSourceInfo, token: string) {
    if (!style) {
        return;
    }

    const source = style.sources[sourceId];
    if (!source || !('tiles' in source) || !source.tiles) {
        return;
    }

    for (let i = 0; i < source.tiles.length; i++) {
        const currentUrl = source.tiles[i];
        if (!currentUrl) {
            continue;
        }

        let patchedUrl = patchUrlWithToken(currentUrl, host, info.file, token);
        if (!patchedUrl) {
            continue;
        }

        if (info.extension) {
            if (patchedUrl.match(/[.]\w*$/)) {
                patchedUrl = patchedUrl.replace(/[.]\w*$/, `.${info.extension}`);
            }
        }

        if (info.encoding && source.type === 'vector') {
            source.encoding = info.encoding;
        }

        source.tiles[i] = patchedUrl;
    }
}

function updateSources(style: StyleSpecification, urlReplacement: MapSourceUrlReplacementInfo): void {
    if (!style) {
        return;
    }

    const { host, nav, base } = urlReplacement;
    if (!host) {
        return;
    }

    if (nav) {
        updateSourceUrl(style, 'festatic', host, nav, 'FEStatic-AAABC-YYYYMMDD-YYYYMMDDHHMMSS');
    }

    if (base) {
        updateSourceUrl(style, 'febase', host, base, 'FEBase-A-YYYYMMDD');
    }
}

function updateSpriteUrls(style: StyleSpecification) {
    // Maplibre expects an absolute URL, this is for no apparent reason and there are open issues on Github about this
    // Need to transform the urls first to include the current document location in the url
    if (!style.sprite) {
        return;
    }

    if (typeof style.sprite === 'string') {
        if (!URL.canParse(style.sprite)) {
            style.sprite = new URL(style.sprite, document.location.href).toString();
        }
    } else {
        style.sprite = style.sprite.map((sprite) => {
            if (typeof sprite === 'object') {
                if (!URL.canParse(sprite.url)) {
                    sprite.url = new URL(sprite.url, document.location.href).toString();
                    return sprite;
                }
            } else {
                return sprite;
            }

            return sprite;
        });
    }

    if (style.glyphs) {
        if (!URL.canParse(style.glyphs)) {
            const base = document.location.host.startsWith('localhost:')
                ? 'https://feweb-dev.flightexplorer.com'
                : document.location.origin;
            style.glyphs = base + '/' + style.glyphs;
        }
    }
}

export function tokenReplaceStyle(style: StyleSpecification, urlReplacement: MapSourceUrlReplacementInfo) {
    updateSources(style, urlReplacement);
    updateSpriteUrls(style);
}
